import { computed, ref } from 'vue'
import type { Product } from '@scayle/storefront-nuxt'
import { applyOdoscopeCookies } from '@goldner-group/storefront-odoscope'
import type {
  OdoscopeEvent,
  OdoscopeSearchQuery,
  OdoscopeSearchResult,
  OdoscopeFilter,
} from '@goldner-group/storefront-odoscope/types'
import useOdoscopeSearchFilterActions from './useOdoscopeSearchFilterActions'
import useOdoscopeSearchAppliedFilters from './useOdoscopeSearchAppliedFilters'
import {
  useState,
  useRoute,
  type AsyncDataRequestStatus,
} from '#app/composables'

import { useRpc } from '#storefront/composables'

import useParameterMemory from '#layers/base/composables/useParameterMemory'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

import { PRODUCTS_PER_PAGE } from '~/constants/product'
import {
  DEFAULT_PRICE_PROMOTION_KEY,
  DEFAULT_PRODUCT_WITH,
} from '#layers/base/constants/withParameters'
import { transformFiltersToOdoscopeQuery } from '#layers/base/utils/odoscopeFilter'

export default () => {
  const productsPerPage = PRODUCTS_PER_PAGE

  const log = useLog('useOdoscope')

  const route = useRoute()
  const { params: utmParams } = useParameterMemory('utm_')

  const {
    trackOdoscopeTimeout,
    trackOdoscopeError,
    trackOdoscopeControlGroup,
    trackOdoscopeEmptyResponse,
    trackOdoscopeSuccess,
  } = useTrackingEvents(true)

  const term = computed(() => {
    const value = Array.isArray(route.query.term)
      ? route.query.term[0]
      : route.query.term

    return value || ''
  })
  // #2 fix: use a stable key so useState doesn't create orphaned state per term
  const stateKey = 'useOdoscopeSearch'
  const sort = computed(() => {
    const value = Array.isArray(route.query.sort)
      ? route.query.sort[0]
      : route.query.sort

    return typeof value === 'string' ? value : undefined
  })

  const pageQuery = computed(() =>
    Array.isArray(route.query.page) ? route.query.page[0] : route.query.page,
  )

  // +----------+
  // | products |
  // +----------+
  const products = useState<Product[]>(`${stateKey}-products`, () => [])
  // Note: Storing the entire error object may cause stringify errors when serializing the useState payload.
  const error = useState(`${stateKey}-error`, () => false)

  // +----------+
  // | filters  |
  // +----------+
  const { appliedFilter, appliedAttributeValues, appliedBooleanValues } =
    useOdoscopeSearchAppliedFilters()
  const {
    applyFilters,
    resetFilter,
    resetPriceFilter,
    resetFilters,
    applyAttributeFilter,
    applyBooleanFilter,
    applyPriceFilter,
  } = useOdoscopeSearchFilterActions()

  const filters = useState<OdoscopeFilter[]>(`${stateKey}-filters`, () => [])
  const unfilteredFilters = useState<OdoscopeFilter[]>(
    `${stateKey}-unfilteredFilters`,
    () => [],
  )
  // #3 fix: track which term the unfiltered filters belong to
  const unfilteredFiltersTerm = useState<string>(
    `${stateKey}-unfilteredFiltersTerm`,
    () => '',
  )
  // Cache the unfiltered total so it survives across fetchProducts calls
  // (e.g. when a filter is removed and totalObjectsCount is missing).
  const cachedUnfilteredTotal = useState<number | undefined>(
    `${stateKey}-unfilteredTotal`,
    () => undefined,
  )

  // +------------+
  // | pagination |
  // +------------+
  const resultsCount = useState(`${stateKey}-total`, () => 0)
  const isFirstPage = computed(() => pagination.value?.page === 1)
  const page = computed(() => (pageQuery.value ? parseInt(pageQuery.value) : 1))

  const pageCount = computed(() =>
    Math.ceil(resultsCount.value / productsPerPage),
  )

  const pagination = computed(() =>
    resultsCount.value > 1
      ? {
          page: page.value,
          first: 1,
          last: pageCount.value,
        }
      : undefined,
  )

  // +----------+
  // | tracking |
  // +----------+
  const getTrackingLabel = (event?: OdoscopeEvent) =>
    event?.label || event?.tokenSlot || 'search'

  const handleOdoscopeEventTracking = (event?: OdoscopeEvent) => {
    if (!event) {
      return
    }

    const label = getTrackingLabel(event)

    if (event.action === 'control') {
      trackOdoscopeControlGroup(label)
      return
    }

    if (event.action === 'empty') {
      trackOdoscopeEmptyResponse(label)
      return
    }

    if (event.action === 'timeout') {
      trackOdoscopeTimeout(label)
      return
    }

    if (event.action === 'error') {
      trackOdoscopeError(label)
      return
    }

    trackOdoscopeSuccess(label)
  }

  const handleErrorResponseTracking = (err: any) => {
    if (err?.response?.status === 408 || err?.status === 408) {
      trackOdoscopeTimeout('search')
    } else {
      trackOdoscopeError('search')
    }
  }

  // +---------------+
  // | data fetching |
  // +---------------+
  const status = ref<AsyncDataRequestStatus>('idle')

  const searchRequest = async (
    query: OdoscopeSearchQuery,
  ): Promise<OdoscopeSearchResult | undefined> =>
    log.time('Fetching odoscope products', async () => {
      const {
        data,
        error: rpcError,
        refresh,
      } = useRpc(
        'getOdoscopeSearchProducts',
        `getOdoscopeSearchProducts-${JSON.stringify(query)}`,
        query,
        { immediate: false, lazy: false, server: false } as any,
      )

      await refresh()

      if (rpcError.value) {
        throw rpcError.value
      }

      applyOdoscopeCookies(data.value?.oscCookie)
      return data.value || undefined
    })

  const fetchProducts = async () => {
    status.value = 'pending'
    error.value = false
    products.value = []

    try {
      const filtersAsQuery = transformFiltersToOdoscopeQuery(
        appliedFilter.value,
      )

      const currentTerm = term.value

      const query = {
        ...utmParams.value,
        q: currentTerm,
        limit: productsPerPage,
        offset: (page.value - 1) * productsPerPage,
        sort: sort.value,
        with: DEFAULT_PRODUCT_WITH,
        pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
        referrer: import.meta.client
          ? sessionStorage?.getItem('referrer')
          : null,
        ...filtersAsQuery,
      }

      log.debug(
        `Fetching product-IDs from odoscope by term: ${currentTerm}`,
        query,
      )

      // #3 fix: reset unfiltered filters when search term changes
      const termChanged = unfilteredFiltersTerm.value !== currentTerm
      if (termChanged) {
        unfilteredFilters.value = []
        cachedUnfilteredTotal.value = undefined
        unfilteredFiltersTerm.value = currentTerm
      }

      // #6 fix: check if we need the unfiltered request and run both in parallel
      const needsUnfilteredRequest = unfilteredFilters.value.length === 0

      const [response, unfilteredResponse] = await Promise.all([
        searchRequest(query),
        needsUnfilteredRequest
          ? searchRequest({
              q: currentTerm,
              limit: productsPerPage,
              offset: 0,
              sort: sort.value,
              with: DEFAULT_PRODUCT_WITH,
              pricePromotionKey: DEFAULT_PRICE_PROMOTION_KEY,
            })
          : Promise.resolve(undefined),
      ])

      if (unfilteredResponse) {
        unfilteredFilters.value = unfilteredResponse.filters || []
        // The unfiltered response is always offset=0 so matchCount is reliable here
        const total =
          unfilteredResponse.originalResponse?.additionalInfo
            ?.totalObjectsCount ||
          (unfilteredResponse as any)?.total ||
          unfilteredResponse.originalResponse?.slices?.[0]?.matchCount
        if (total != null) {
          cachedUnfilteredTotal.value = total
        }
      }

      handleOdoscopeEventTracking(response?.odoscopeEvent)

      const hasActiveFilters =
        (appliedFilter.value.attributes?.length ?? 0) > 0 ||
        appliedFilter.value.minPrice != null ||
        appliedFilter.value.maxPrice != null

      // totalObjectsCount is the global unfiltered total — do NOT use it when
      // filters are active, otherwise the count stays at e.g. 130 instead of 13.
      const globalTotal =
        response?.originalResponse?.additionalInfo?.totalObjectsCount ||
        (response as any)?.total

      if (hasActiveFilters) {
        // matchCount reflects the filtered result count but is only reliable
        // at offset=0 (page 1). On subsequent pages it may return per-slice
        // counts which would shrink the total and hide pagination.
        // → Only update on page 1, keep cached value on page 2+.
        if (page.value === 1) {
          const filteredTotal =
            response?.originalResponse?.slices?.[0]?.matchCount
          if (filteredTotal != null) {
            resultsCount.value = filteredTotal
          }
        }
      } else if (cachedUnfilteredTotal.value != null) {
        resultsCount.value = cachedUnfilteredTotal.value
      } else if (globalTotal != null) {
        resultsCount.value = globalTotal
      } else if (resultsCount.value === 0) {
        // Last resort: use matchCount only when we have no value at all
        const matchCount = response?.originalResponse?.slices?.[0]?.matchCount
        if (matchCount != null) {
          resultsCount.value = matchCount
        }
      }
      products.value = response?.products || []
      // Always show the full (unfiltered) filter list so filters don't disappear
      // when narrowing down results. Only fall back to the response filters on
      // the very first load when unfilteredFilters haven't been populated yet.
      filters.value = unfilteredFilters.value.length
        ? unfilteredFilters.value
        : response?.filters || []

      // #8 fix: only set error when there are no results AND no filters active.
      // Zero results with active filters is a legitimate "no matches" state.
      if (!products.value?.length && !hasActiveFilters) {
        error.value = true
        status.value = 'error'
      } else {
        status.value = 'success'
      }
    } catch (err) {
      handleError(err)
    }
  }

  // +----------------+
  // | error handling |
  // +----------------+
  const handleError = (err: unknown) => {
    handleErrorResponseTracking(err)
    error.value = true
    status.value = 'error'
  }

  return {
    fetchProducts,
    products,
    resultsCount,
    productsPerPage,

    applyFilters,
    resetFilter,
    resetPriceFilter,
    resetFilters,
    applyAttributeFilter,
    applyBooleanFilter,
    applyPriceFilter,
    filters,
    appliedFilter,
    appliedAttributeValues,
    appliedBooleanValues,
    unfilteredFilters,

    page,
    pagination,
    isFirstPage,

    error,
    status,
  }
}
